import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Layers,
  HardDrive,
  BookOpen,
  FileText,
  MoreVertical,
  CheckCircle2,
  XCircle,
  Clock,
  HelpCircle,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DocumentMappingsDialog } from './DocumentMappingsDialog';
import type { ConnectorConfig, ConnectorType } from '@/types/api';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface ConnectorCardProps {
  readonly config: ConnectorConfig;
}

const CONNECTOR_ICON: Record<ConnectorType, { icon: typeof Layers; color: string }> = {
  sharepoint: { icon: Layers, color: 'text-blue-400' },
  google_drive: { icon: HardDrive, color: 'text-green-400' },
  confluence: { icon: BookOpen, color: 'text-blue-400' },
  policystat: { icon: FileText, color: 'text-orange-400' },
};

const VERIFICATION_BADGE: Record<string, { icon: typeof CheckCircle2; label: string; className: string }> = {
  verified: { icon: CheckCircle2, label: 'Verified', className: 'text-green-400' },
  failed: { icon: XCircle, label: 'Failed', className: 'text-red-400' },
  pending: { icon: Clock, label: 'Pending', className: 'text-amber-400' },
  unchecked: { icon: HelpCircle, label: 'Not checked', className: 'text-muted-foreground' },
};

function formatRelativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${diffDays}d ago`;
}

export function ConnectorCard({ config }: ConnectorCardProps) {
  const queryClient = useQueryClient();
  const [mappingsOpen, setMappingsOpen] = useState(false);

  const iconCfg = CONNECTOR_ICON[config.connectorType];
  const Icon = iconCfg.icon;
  const verifCfg = VERIFICATION_BADGE[config.verificationStatus];
  const VerifIcon = verifCfg.icon;

  const verifyMutation = useMutation({
    mutationFn: () => api.admin.connectors.verify(config.configId),
    onSuccess: () => {
      toast.success('Connection verified.');
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
    onError: () => toast.error('Verification failed.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.admin.connectors.delete(config.configId),
    onSuccess: () => {
      toast.success('Connector removed.');
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
    onError: () => toast.error('Failed to remove connector.'),
  });

  return (
    <>
      <Card>
        <CardContent className="flex items-center gap-4 p-5">
          <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg bg-muted', iconCfg.color)}>
            <Icon className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">{config.displayName}</p>
            <p className="text-xs capitalize text-muted-foreground">
              {config.connectorType.replace('_', ' ')}
            </p>
            <p className="text-xs text-muted-foreground">
              Last verified: {config.lastVerifiedAt ? formatRelativeTime(config.lastVerifiedAt) : 'Never'}
            </p>
          </div>

          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className={cn('flex items-center gap-1 text-xs font-medium', verifCfg.className)}>
              <VerifIcon className="h-3.5 w-3.5" />
              {verifCfg.label}
            </span>
            <span className="text-xs text-muted-foreground">
              {config.documentMappings.length} documents mapped
            </span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => verifyMutation.mutate()}
                disabled={verifyMutation.isPending}
              >
                {verifyMutation.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                Verify Connection
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setMappingsOpen(true)}>
                View Mappings
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-400 focus:text-red-400"
                onClick={() => {
                  if (window.confirm(`Remove ${config.displayName}?`)) {
                    deleteMutation.mutate();
                  }
                }}
              >
                Remove Connector
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardContent>
      </Card>

      <DocumentMappingsDialog
        open={mappingsOpen}
        onOpenChange={setMappingsOpen}
        config={config}
      />
    </>
  );
}
